import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useBalanceStore } from "@shared/store/balance.store";
import { Alert, AlertDescription } from "@shared/ui/components/alert";
import { Button } from "@shared/ui/components/button";
import { CustomSelect, type CustomSelectOption } from "@shared/ui/components/custom-select";
import { Label } from "@shared/ui/components/label";
import { Progress } from "@shared/ui/components/progress";
import { RadioGroup, RadioGroupItem } from "@shared/ui/components/radio-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/ui/components/tooltip";
import { LoadingDots } from "@shared/ui/LoadingDots";
import { AnimatePresence, motion } from "framer-motion";
import { Clock, Sparkles, Video, X, Zap } from "lucide-react";

const LANGUAGE_OPTIONS: CustomSelectOption[] = [
  { value: "auto", label: "🤖 Автоопределение" },
  { value: "ru", label: "Русский" },
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
  { value: "it", label: "Italiano" },
  { value: "pt", label: "Português" },
  { value: "zh", label: "中文" },
  { value: "ja", label: "日本語" },
  { value: "ko", label: "한국어" },
  { value: "ar", label: "العربية" },
  { value: "hi", label: "हिन्दी" },
  { value: "tr", label: "Türkçe" },
  { value: "pl", label: "Polski" },
  { value: "uk", label: "Українська" },
  { value: "kk", label: "Қазақша" },
  { value: "uz", label: "Oʻzbekcha" },
  { value: "hy", label: "Հայերեն" },
  { value: "ka", label: "ქართული" }
];

/**
 * Языки, которые поддерживает только модель повышенной точности (AssemblyAI Universal-2).
 * В быстром режиме (Deepgram nova-3) их нет, поэтому для них принудительно включаем «Качество».
 */
const QUALITY_ONLY_LANGUAGES = ["kk", "uz", "hy", "ka"];

interface FileSelectProps {
  file: File;
  onRemoveFile: () => void;
  onTranscribe: () => void;
  model: "speed" | "quality";
  setModel: (model: "speed" | "quality") => void;
  language: string;
  setLanguage: (language: string) => void;
  isAuthenticated: boolean;
  uploading: boolean;
  progress: number;
  uploadFinished: boolean;
  estimatedMinutes: number | null;
  isFileTooLarge: boolean;
  maxFileSizeText?: string;
  isLoadingMetadata?: boolean;
  isTranscribing?: boolean;
  isPreviewMode?: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const FileSelect: React.FC<FileSelectProps> = ({
  file,
  onRemoveFile,
  onTranscribe,
  model,
  setModel,
  language,
  setLanguage,
  isAuthenticated,
  uploading,
  progress,
  uploadFinished,
  estimatedMinutes,
  isFileTooLarge,
  maxFileSizeText = "2 ГБ",
  isLoadingMetadata = false,
  isTranscribing = false,
  isPreviewMode = false
}) => {
  const [visualProgress, setVisualProgress] = useState(0);
  const { balance } = useBalanceStore();
  const successMessageRef = useRef<HTMLDivElement>(null);
  const viewResultButtonRef = useRef<HTMLAnchorElement>(null);
  const isQualityOnlyLanguage = QUALITY_ONLY_LANGUAGES.includes(language);

  useEffect(() => {
    if (uploading) {
      setVisualProgress(progress);
    }
  }, [progress, uploading]);

  // Для языков без поддержки в быстрой модели переключаем режим на «Качество»
  useEffect(() => {
    if (isQualityOnlyLanguage && model !== "quality") {
      setModel("quality");
    }
  }, [isQualityOnlyLanguage, model, setModel]);

  // Auto-scroll and focus when upload is finished
  useEffect(() => {
    if (uploadFinished) {
      if (successMessageRef.current) {
        successMessageRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      // Focus the button for better UX
      if (viewResultButtonRef.current) {
        viewResultButtonRef.current.focus({ preventScroll: true });
      }
    }
  }, [uploadFinished]);

  // Условие для показа блока с минутами - показываем при наличии баланса,
  // а для неавторизованных - нейтральный блок с длительностью файла
  const shouldShowMinutesBlock = !isLoadingMetadata && !isTranscribing && (balance || !isAuthenticated);

  // Проверяем достаточно ли минут (для неизвестной длительности резервируем 10 минут).
  // У неавторизованного баланса нет - проверка баланса происходит уже после входа
  const requiredMinutes = estimatedMinutes !== null ? estimatedMinutes : 10;
  const hasEnoughMinutes = isPreviewMode
    ? true
    : !isAuthenticated || !balance
      ? true
      : (balance.availableMinutes ?? balance.totalMinutes) >= requiredMinutes;

  const isUrl = file.type === "video/url";
  const fadeTransition = { duration: 0.15 };

  return (
    <AnimatePresence mode="wait" initial={false}>
      {/* ВАЖНО: Проверяем uploadFinished ПЕРВЫМ, чтобы показать кнопку даже если uploading=true */}
      {uploadFinished ? (
        <motion.div
          key="success"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={fadeTransition}
          ref={successMessageRef}
          className="text-center"
        >
          <h4 className="font-semibold text-foreground mb-6">
            {isUrl ? "Видео отправлено на обработку!" : "Файл успешно загружен!"}
          </h4>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button size="lg" asChild className="flex-1 bg-primary hover:bg-primary-dark min-h-[44px]">
              <Link to="/history" ref={viewResultButtonRef}>
                Посмотреть результат
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="flex-1 bg-transparent min-h-[44px]" onClick={onRemoveFile}>
              {isUrl ? "Добавить еще видео" : "Загрузить новый файл"}
            </Button>
          </div>
        </motion.div>
      ) : uploading ? (
        // Показываем прогресс загрузки только если НЕ завершено
        <motion.div
          key="uploading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={fadeTransition}
          className="space-y-4"
        >
          <div className="flex items-center gap-3">
            <Video className="h-8 w-8 text-primary" />
            <div className="flex-1 min-w-0">
              <div className="overflow-hidden">
                <p className="font-medium truncate">{file.name}</p>
              </div>
              <p className="text-sm text-muted-foreground">Загрузка...</p>
            </div>
          </div>
          <Progress value={visualProgress} className="transition-all duration-150" />
          <p className="text-sm text-center text-muted-foreground">{Math.round(visualProgress)}%</p>
        </motion.div>
      ) : (
        <motion.div
          key="settings"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={fadeTransition}
        >
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Video className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="truncate text-sm font-semibold text-foreground">{file.name}</h4>
              <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onRemoveFile} disabled={uploading}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="mb-4 space-y-3">
            <div>
              <Label className="mb-2 block text-sm font-semibold">Режим обработки</Label>
              <RadioGroup
                value={isPreviewMode ? "speed" : model}
                onValueChange={value => setModel(value as "speed" | "quality")}
                defaultValue="quality"
                className="grid grid-cols-2 gap-3"
              >
                <div>
                  <RadioGroupItem value="speed" id="speed" className="peer sr-only" disabled={isQualityOnlyLanguage} />
                  <Label
                    htmlFor="speed"
                    className={`flex cursor-pointer items-center gap-2 rounded-md border-2 border-border bg-transparent px-3 py-2.5 transition-colors hover:bg-muted peer-data-[state=checked]:border-primary ${
                      isQualityOnlyLanguage ? "cursor-not-allowed opacity-50" : ""
                    }`}
                  >
                    <Zap className="h-4 w-4 shrink-0 text-primary" />
                    <span className="text-sm font-medium">Скорость</span>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="quality" id="quality" className="peer sr-only" disabled={isPreviewMode} />
                  <Label
                    htmlFor="quality"
                    className={`flex cursor-pointer items-center gap-2 rounded-md border-2 px-3 py-2.5 transition-colors hover:bg-muted ${
                      isPreviewMode
                        ? "cursor-not-allowed opacity-50 border-border peer-data-[state=checked]:border-primary"
                        : "border-amber-400 peer-data-[state=checked]:border-amber-500"
                    }`}
                  >
                    <Sparkles className="h-4 w-4 shrink-0 text-amber-500" />
                    <span className="text-sm font-medium">{isPreviewMode ? "Качество (платно)" : "Качество"}</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="language" className="mb-2 block text-sm font-semibold">
                Язык видео
              </Label>
              <CustomSelect
                value={language}
                onChange={setLanguage}
                options={LANGUAGE_OPTIONS}
                placeholder="Выберите язык"
                className="w-full"
              />
              {isQualityOnlyLanguage && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Для этого языка доступен только режим «Качество» — он включён автоматически.
                </p>
              )}
            </div>
          </div>

          <AnimatePresence initial={false}>
            {isLoadingMetadata && (
              <motion.div
                key="loading-metadata"
                layout
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={fadeTransition}
                className="overflow-hidden"
              >
                <Alert className="mb-4">
                  <Clock className="h-4 w-4 animate-spin" />
                  <AlertDescription>Получение метаданных видео...</AlertDescription>
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence initial={false}>
            {isTranscribing && !uploadFinished && (
              <motion.div
                key="transcribing"
                layout
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={fadeTransition}
                className="overflow-hidden"
              >
                <Alert className="mb-4">
                  <Clock className="h-4 w-4 animate-spin" />
                  <AlertDescription>Отправка на обработку...</AlertDescription>
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence initial={false}>
            {shouldShowMinutesBlock && !isAuthenticated && (
              <motion.div
                key="minutes-anonymous"
                layout
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={fadeTransition}
                className="overflow-hidden"
              >
                <Alert className="mb-4">
                  <Clock className="h-4 w-4" />
                  <AlertDescription>
                    {estimatedMinutes !== null ? (
                      <div>
                        Длительность файла: <strong>{estimatedMinutes} мин</strong>
                      </div>
                    ) : (
                      <div>
                        <p>
                          <strong>Не удалось определить продолжительность файла.</strong>
                        </p>
                        <p className="mt-1 text-sm">
                          Зарезервируем 10 минут, после обработки спишем фактическое количество.
                        </p>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence initial={false}>
            {shouldShowMinutesBlock && isAuthenticated && balance && !isPreviewMode && (
              <motion.div
                key="minutes-balance"
                layout
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={fadeTransition}
                className="overflow-hidden"
              >
                <Alert
                  className={`mb-4 ${!hasEnoughMinutes ? "border-2 !bg-red-50" : ""}`}
                  variant={hasEnoughMinutes ? "default" : "destructive"}
                >
                  {hasEnoughMinutes && <Clock className="h-4 w-4" />}
                  <AlertDescription>
                    {estimatedMinutes !== null ? (
                      hasEnoughMinutes ? (
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            Будет использовано <strong>{estimatedMinutes} мин</strong> из{" "}
                            {balance.availableMinutes ?? balance.totalMinutes} мин
                          </div>
                          <div className="whitespace-nowrap">
                            Останется: {(balance.availableMinutes ?? balance.totalMinutes) - estimatedMinutes} мин
                          </div>
                        </div>
                      ) : (
                        <p>
                          Недостаточно минут. У вас:{" "}
                          <strong>{balance.availableMinutes ?? balance.totalMinutes} мин</strong>, требуется:{" "}
                          <strong>{estimatedMinutes} мин</strong>.{" "}
                          <Link to="/pricing" className="underline font-semibold">
                            Докупите минуты
                          </Link>{" "}
                          или выберите другой тариф.
                        </p>
                      )
                    ) : hasEnoughMinutes ? (
                      <p>
                        Не удалось определить продолжительность файла — зарезервируем <strong>10 минут</strong>, после
                        обработки спишем фактическое количество. Доступно:{" "}
                        {balance.availableMinutes ?? balance.totalMinutes} мин.
                      </p>
                    ) : (
                      <p>
                        Не удалось определить продолжительность файла. Недостаточно минут — требуется минимум 10.{" "}
                        <Link to="/pricing" className="underline font-semibold">
                          Докупите минуты
                        </Link>{" "}
                        или выберите другой тариф.
                      </p>
                    )}
                  </AlertDescription>
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex flex-col gap-3 sm:flex-row">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex-1">
                    <Button
                      size="lg"
                      className="w-full bg-primary hover:bg-primary-dark min-h-[44px]"
                      onClick={onTranscribe}
                      disabled={uploading || isFileTooLarge || isLoadingMetadata || isTranscribing || !hasEnoughMinutes}
                    >
                      {uploading ? (
                        <>
                          Загрузка
                          <LoadingDots />
                        </>
                      ) : isLoadingMetadata ? (
                        <>
                          Получение данных
                          <LoadingDots />
                        </>
                      ) : isTranscribing ? (
                        <>
                          Отправка
                          <LoadingDots />
                        </>
                      ) : isAuthenticated ? (
                        "Начать транскрипцию"
                      ) : (
                        "Транскрибировать"
                      )}
                    </Button>
                  </div>
                </TooltipTrigger>
                {isFileTooLarge && (
                  <TooltipContent>
                    <p>Файл слишком большой. Максимальный размер: {maxFileSizeText}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            <Button
              size="lg"
              variant="outline"
              className="flex-1 bg-transparent min-h-[44px]"
              onClick={onRemoveFile}
              disabled={uploading || isLoadingMetadata || isTranscribing}
            >
              Выбрать другой файл
            </Button>
          </div>

          {!isAuthenticated && (
            <p className="mt-4 text-center text-sm text-muted-foreground">
              Файл сохранится - после входа обработка продолжится автоматически
            </p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
